import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { IArticleNode, IMediaCache } from "../models/interface";
import { getArticleToSelect } from "../transformers";

export interface IUserGuideState {
	isUserGuideModalVisible: boolean;
	articles: IArticleNode[];
	articleLookupMap: Record<string, IArticleNode>;
	selectedArticle: IArticleNode | null;
	searchText: string;
	sharedArticleId?: string;
	loading: {
		leftPanel: boolean;
		rightPanel: boolean;
	};
	mediaCache: {
		[articleId: string]: IMediaCache;
	};
	mediaLoading: boolean;
}

export const initialState: IUserGuideState = {
	isUserGuideModalVisible: false,
	articles: [],
	articleLookupMap: {},
	selectedArticle: null,
	searchText: "",
	sharedArticleId: null,
	loading: {
		leftPanel: false,
		rightPanel: false,
	},
	mediaCache: {},
	mediaLoading: false,
};

const userGuideSlice = createSlice({
	name: "userGuide",
	initialState,
	reducers: {
		setIsUserGuideModalVisible(state, action: PayloadAction<boolean>) {
			state.isUserGuideModalVisible = action.payload;
		},
		setArticles(state, action: PayloadAction<IArticleNode[]>) {
			state.articles = action.payload;
			const lookup: Record<string, IArticleNode> = {};
			for (const article of action.payload) {
				lookup[article.key] = article;
			}
			state.articleLookupMap = lookup;
		},
		setSelectedArticleByKey(state, action: PayloadAction<string>) {
			const article = state.articleLookupMap[action.payload];
			state.selectedArticle = article ? getArticleToSelect(article) : null;
		},
		setSelectedArticle(state, action: PayloadAction<IArticleNode | null>) {
			state.selectedArticle = action.payload;
		},
		setSearchText(state, action: PayloadAction<string>) {
			state.searchText = action.payload;
		},
		setSharedArticleId(state, action: PayloadAction<string | null>) {
			state.sharedArticleId = action.payload || undefined;
		},
		updateLoadingState(state, action: PayloadAction<Partial<IUserGuideState["loading"]>>) {
			state.loading = { ...state.loading, ...action.payload };
		},
		setMediaCache(state, action: PayloadAction<{ articleId: string; cache: IMediaCache }>) {
			const { articleId, cache } = action.payload;
			state.mediaCache[articleId] = cache;
		},
		setMediaLoading(state, action: PayloadAction<boolean>) {
			state.mediaLoading = action.payload;
		},
		clearExpiredMediaCache(state) {
			const now = Date.now();
			Object.keys(state.mediaCache).forEach((articleId) => {
				if (state.mediaCache[articleId].expiresAt < now) {
					delete state.mediaCache[articleId];
				}
			});
		},
	},
});

export const {
	setIsUserGuideModalVisible,
	setArticles,
	setSelectedArticle,
	setSelectedArticleByKey,
	setSearchText,
	setSharedArticleId,
	updateLoadingState,
	setMediaCache,
	setMediaLoading,
	clearExpiredMediaCache,
} = userGuideSlice.actions;

export default userGuideSlice.reducer;
